<script setup lang="ts">
import { ref } from 'vue';
import { formatHelpers, AccordionContent } from '@hfc/ui-core';
import { useCarrierInfo } from '@/application/shared/infrastructure/composition/useCarrierInfo';
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import { ContributionType } from '@/application/smallBusiness/domain/SmallBusinessContribution';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import PlanDetails from '@/planList/infrastructure/components/PlanDetails.vue';
import AccordionPricingDetails from '@/application/smallBusiness/infrastructure/components/AccordionPricingDetails.vue';

defineProps<{
  plan: SmallBusinessPlan;
  applicationType: APPLICATION_TYPES;
  contributionType?: ContributionType;
  employeeContribution?: number;
  dependentContribution?: number;
}>();

const planDetailsVisible = ref<boolean>(true);

const { getCarrierLogoId } = useCarrierInfo();
const { formatPrice } = formatHelpers();

const ignoredDetails = ['PricingDetails', 'TotalEmployerContribution', 'EstimatedTotalCost'];

</script>
<template>
  <div class="space-y-3 lining-nums">
    <div class="flex flex-col sm:flex-row px-4 bg-blue-100 rounded-md shadow-md">
      <div class="flex items-center justify-center sm:justify-start mt-4 sm:mt-0">
        <carrier-logo class="w-56 lg:w-72 px-6 flex items-center" :carrier-id="getCarrierLogoId(plan)" :coverage-type="applicationType" vertical="true" no-link="true" />
      </div>
      <div class="flex flex-col space-y-4 py-6">
        <div class="flex">
          <h2 class="font-semibold text-2xl text-blue-600 lining-nums">{{ plan.name }}</h2>
          <!-- Here we should review if we need twoLineName -->
        </div>
        <div class="flex font-bold text-4xl text-red animate-[bounce_500ms_ease-out] transition-all duration-300">
          <span>{{ formatPrice(plan.rate) }}</span><span class="text-lg mt-2">/month</span>
        </div>
      </div>
    </div>

    <AccordionPricingDetails :plan="plan" :application-type="applicationType" :dependent-contribution="dependentContribution" :employee-contribution="employeeContribution" :contribution-type="contributionType" />

    <AccordionContent v-model:open="planDetailsVisible" open-icon="chevron-up" closed-icon="chevron-down" open-icon-color="text-blue-600" open-class="bg-slate-200">
      <template #title>
        <div class="text-lg font-semibold text-blue-600">
          Benefits
        </div>
      </template>
      <template #content>
        <PlanDetails :plan="plan" :application-type="applicationType" :ignored-details="ignoredDetails" :hide-plan-banner="true" />
      </template>
    </AccordionContent>
  </div>
</template>
